import styled from 'styled-components';
import { ReactComponent as DeleteIcon } from '@elementary/common-ui/assets/16x16/delete-trash.svg';
import { ReactComponent as EditIcon } from '@elementary/common-ui/assets/edit.svg';
import {
  Chip,
  Flex,
  FlexCenter,
  FlexSpace,
  IconButton,
  OverflowTooltip,
  StatusChip,
} from '@elementary/common-ui/components';
import { ChipStatus } from '@elementary/common-ui/components/Chip/StatusChip';
import useEnvPermission from '@elementary/common-ui/hooks/useEnvPermission';
import { Tooltip } from '@mui/material';
import {
  IntegrationType,
  AlertDestinationConditions,
  ConfigurationType,
} from 'shared/types';
import AlertDestinationIcon from './AlertDestinationIcon';

const configTypeLabelMap: Record<string, string> = {
  CLOUD: 'rule',
  CODE: 'code',
  CUSTOM: 'custom',
};

const tooltipTextMap: Record<string, string> = {
  CLOUD: 'Configured in rule',
  CODE: 'Configured in code',
  CUSTOM: 'Custom configuration',
};

const mapStatusToChipStatus = (status: string): ChipStatus | undefined => {
  const statusMap: Record<string, ChipStatus> = {
    FAIL: 'fail',
    ERROR: 'error',
    WARN: 'warn',
    RUNTIME_ERROR: 'runtime_error',
  };
  return statusMap[status.toUpperCase()];
};

const Wrapper = styled(FlexSpace)`
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.palette.common.gray5};
  border-radius: 8px;
`;

const EditIconStyled = styled(EditIcon)`
  width: 12px;
`;

export type Props = {
  type: IntegrationType;
  channel: string;
  configurationType?: ConfigurationType;
  conditions?: AlertDestinationConditions;
  showEdit?: boolean;
  showDelete?: boolean;
  onEdit: () => void;
  onDelete: () => void;
};

const AlertDestinationItem = ({
  type,
  channel,
  configurationType,
  conditions,
  showEdit = true,
  showDelete = true,
  onEdit,
  onDelete,
}: Props) => {
  const hasPermission = useEnvPermission();

  const hasWritePermission = hasPermission('EDIT_ALERT_RULES');

  const statuses = conditions?.statuses || [];

  return (
    <Wrapper>
      <Flex sx={{ alignItems: 'center', gap: 1, overflow: 'hidden' }}>
        <FlexCenter sx={{ flexShrink: 0 }}>
          <AlertDestinationIcon type={type} />
        </FlexCenter>
        <OverflowTooltip
          typographySx={{
            fontSize: '14px !important',
            fontWeight: 'bold !important',
          }}
        >
          {channel}
        </OverflowTooltip>

        {configurationType && (
          <Tooltip title={tooltipTextMap[configurationType]}>
            <div style={{ flexShrink: 0 }}>
              <Chip
                variant="outlined"
                label={configTypeLabelMap[configurationType].toUpperCase()}
                size="small"
                bold
              />
            </div>
          </Tooltip>
        )}

        {statuses.length > 0 && (
          <Tooltip title={`Triggers on: ${statuses.join(', ')}`}>
            <Flex sx={{ gap: 0.5, flexShrink: 0 }}>
              {statuses
                .map((status: string) => ({
                  status,
                  chipStatus: mapStatusToChipStatus(status),
                }))
                .filter(({ chipStatus }) => chipStatus !== undefined)
                .map(({ status, chipStatus }) => (
                  <span key={status}>
                    <StatusChip status={chipStatus!} size="small" />
                  </span>
                ))}
            </Flex>
          </Tooltip>
        )}
      </Flex>

      {hasWritePermission && (
        <FlexCenter sx={{ gap: 1 }}>
          {showEdit && (
            <IconButton onClick={onEdit}>
              <EditIconStyled />
            </IconButton>
          )}

          {showDelete && (
            <IconButton onClick={onDelete}>
              <DeleteIcon />
            </IconButton>
          )}
        </FlexCenter>
      )}
    </Wrapper>
  );
};

export default AlertDestinationItem;
